import cn from 'classnames';
import { FocusEvent, useEffect, useState } from 'react';

import Legend from '@components/controls/Legend';
import { PriceUnitLabel } from '@components/uiKit/PriceUnitLabel';

import { PRICE_UNITS } from '@scripts/enums';
import { VisuallyHidden } from '@scripts/gds';

import MinusIcon from '@icons/24/minus.svg';
import PlusIcon from '@icons/24/plus.svg';

import { COUNTER_ROW_HEIGHT_PX } from './constants';
import styles from './Counter.module.scss';
import type { CounterProps, CounterSize } from './types';

export type { CounterProps, CounterSize };
export { COUNTER_ROW_HEIGHT_PX };

const Counter = ({
    name,
    initialValue = 1,
    value,
    label,
    step = 1,
    min = 0,
    max = 999,
    onChange,
    isHiddenLegend = true,
    required,
    hint,
    field,
    meta,
    helpers,
    disabled = false,
    isFullWidth = false,
    isDetailCard = false,
    size,
    priceUnit = PRICE_UNITS.PIECE,
    isBasketPage = false,
    className,
    rowDensity,
    hideMultiplicity,
    ...restProps
}: CounterProps) => {
    const resolvedDensity: CounterSize = size ?? 'large';
    const isSmallRow = resolvedDensity === 'small';
    const isMediumRow = resolvedDensity === 'medium';
    const isLargeRow = resolvedDensity === 'large';
    const isPrimaryDensity = rowDensity === 'primary';
    const [innerValue, setInnerValue] = useState<string | number>(() =>
        typeof value === 'number' ? value : initialValue
    );

    const stepDigitsAfterDecimal = `${step}`.split('.')[1]?.length;
    const stepWithDot = step?.toString().replace('.', ',');
    const rowSizeClass = cn(
        isSmallRow && styles.counter_rowSmall,
        isMediumRow && styles.counter_rowMedium,
        isLargeRow && styles.counter_rowLarge
    );
    const buttonClassName = cn(
        styles.counter_button,
        isSmallRow && styles.counter_buttonSmall,
        isMediumRow && styles.counter_buttonMedium,
        isLargeRow && styles.counter_buttonLarge,
        isPrimaryDensity && styles.counter_buttonPrimaryDensity
    );

    const remainderDivisionFractionalNumbers = (numerator: string | number, denominator: number) => {
        const precision = Math.pow(10, stepDigitsAfterDecimal || 1);

        const wholeNumerator = Math.round(+numerator * precision);
        const wholeDenominator = Math.round(denominator * precision);

        const remainder = wholeNumerator % wholeDenominator;

        return remainder / precision;
    };

    const changeValue = (newValue: number) => {
        let resolvedQty: number | string;

        if (Number.isInteger(step)) {
            switch (true) {
                case newValue >= max:
                    resolvedQty = max;
                    break;
                case newValue % step === 0:
                    resolvedQty = newValue;
                    break;
                default: {
                    const valueWithStep = newValue + (step - (newValue % step));
                    resolvedQty = valueWithStep > max ? max : valueWithStep;
                }
            }
        } else {
            switch (true) {
                case newValue >= max:
                    resolvedQty = max;
                    break;
                case remainderDivisionFractionalNumbers(innerValue, step) === 0:
                    resolvedQty = newValue;
                    break;
                default: {
                    const roundingUp = (Math.ceil(newValue / step) * step).toFixed(stepDigitsAfterDecimal);
                    resolvedQty = +roundingUp > max ? max : roundingUp;
                }
            }
        }
        setInnerValue(resolvedQty);

        const numericValue = Number(resolvedQty);
        const fractionDigits = stepDigitsAfterDecimal ?? 0;

        if (numericValue.toFixed(fractionDigits) === (value ?? initialValue).toFixed(fractionDigits)) {
            return;
        }

        if (helpers) {
            helpers.setValue(numericValue);
        }

        if (onChange) {
            onChange(numericValue);
        }
    };

    const handleChangeInputValue = (value: string) => {
        const newValue = +value;
        if (newValue < min) {
            changeValue(min);

            return;
        }
        if (newValue > max) {
            changeValue(max);

            return;
        }
        changeValue(newValue);
    };

    const handleInputBlur = ({ target }: FocusEvent<HTMLInputElement>) => {
        const fractionDigits = stepDigitsAfterDecimal ?? 0;

        handleChangeInputValue(Number(target.value).toFixed(fractionDigits));
    };

    useEffect(() => {
        if (typeof value === 'number') {
            setInnerValue(value);
        }
    }, [value]);

    return (
        <div {...restProps} className={cn(styles.counter, className)}>
            {isHiddenLegend ? (
                <VisuallyHidden>
                    <Legend name={name} label={label} required={required} hint={hint} meta={meta} />
                </VisuallyHidden>
            ) : (
                <Legend name={name} label={label} required={required} hint={hint} meta={meta} />
            )}
            <div className={cn(styles.counter_controlsColumn, isBasketPage && styles.counter_controlsColumnBasket)}>
                <div className={cn(styles.counter_row, rowSizeClass)}>
                    <button
                        type="button"
                        className={buttonClassName}
                        aria-label={`Уменьшить на ${step}`}
                        onClick={() => changeValue(+(+innerValue - +step).toFixed(stepDigitsAfterDecimal))}
                        disabled={Number(innerValue) < min + step || disabled}
                        title={`Уменьшить на ${step}`}
                    >
                        <MinusIcon className={styles.counter_icon} />
                    </button>
                    <div
                        className={cn(
                            styles.counter_inputWrap,
                            isDetailCard && isMediumRow && styles.counter_inputWrapDetailCompact
                        )}
                    >
                        <input
                            type="number"
                            name={name}
                            id={name}
                            {...field}
                            value={innerValue}
                            step={step}
                            onChange={({ target }) =>
                                target.value ? setInnerValue(Number(target.value)) : setInnerValue('')
                            }
                            onBlur={handleInputBlur}
                            onClick={({ currentTarget }) => currentTarget.select()}
                            className={cn(
                                styles.counter_inputField,
                                isSmallRow && styles.counter_inputFieldSmall,
                                isLargeRow && styles.counter_inputFieldLarge,
                                isDetailCard && isMediumRow && styles.counter_inputFieldDetailCompact,
                                isPrimaryDensity && styles.counter_inputFieldPrimaryDensity
                            )}
                        />
                    </div>
                    <button
                        type="button"
                        className={buttonClassName}
                        aria-label={`Увеличить на ${step}`}
                        onClick={() => changeValue(+(+innerValue + +step).toFixed(stepDigitsAfterDecimal))}
                        disabled={Number(innerValue) >= max || disabled}
                        title={`Увеличить на ${step}`}
                    >
                        <PlusIcon className={styles.counter_icon} />
                    </button>
                </div>
                {step > 1 && !isFullWidth && !hideMultiplicity && (
                    <p className={cn(styles.counter_multiplicity, isBasketPage && styles.counter_multiplicityBasket)}>
                        <span className={styles.counter_multiplicityLabel}>Кратность</span>{' '}
                        <span
                            className={cn(
                                styles.counter_multiplicityValue,
                                isBasketPage && styles.counter_multiplicityValueBasket
                            )}
                        >
                            {stepWithDot}
                        </span>{' '}
                        <span
                            className={cn(
                                styles.counter_multiplicityValue,
                                isBasketPage && styles.counter_multiplicityValueBasket
                            )}
                        >
                            <PriceUnitLabel priceUnit={priceUnit} quantity={step} />
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};

export default Counter;
